import { useCallback, useEffect, useState } from "react";
import { getActivityLogsURL, getTecfolkActivityLogsURL } from "../../../libraries/apiUrl";
import { _get } from "../../../libraries/fetch";

const TECFOLK_LOGIN_USERS = ["Tecgym Admin", "Tecfolk"];

function normalizeLoginUserName(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function getLogLoginUserNames(log) {
  return [
    log?.loginUserName,
    log?.loginUsername,
    log?.userName,
    log?.username,
    log?.name,
    log?.activityBy
  ]
    .map(normalizeLoginUserName)
    .filter(Boolean);
}

function keepTecfolkLoginUserActivities(logs) {
  const allowedNames = new Set(TECFOLK_LOGIN_USERS.map(normalizeLoginUserName));
  return logs.filter((log) => getLogLoginUserNames(log).some((name) => allowedNames.has(name)));
}

function extractLogList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.response)) return data.response;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

function getLogTime(log) {
  const value = log?.createdDate || log?.activityDate || log?.date || log?.createdAt || log?.timestamp || log?.activity_date;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

async function fetchTecfolkActivityLogs(type, by, formattedFrom, formattedTo) {
  const userNames = !by || by === "All" ? TECFOLK_LOGIN_USERS : [by];
  const query = new URLSearchParams({
    activityType: type,
    activityBy: by || "All",
    dateFrom: formattedFrom,
    dateTo: formattedTo,
    loginUserName: userNames.join(","),
  });
  const data = await _get(
    `${getTecfolkActivityLogsURL()}?${query.toString()}`,
    { useCache: false, httpCache: "no-store" }
  );

  if (!data) {
    throw new Error("Failed to load activity logs.");
  }

  return keepTecfolkLoginUserActivities(extractLogList(data))
    .map((log) => ({
      ...log,
      clientName: log.clientName || log.gymName || log.branchName,
      branchName: log.branchName || log.gymName,
    }))
    .sort((left, right) => getLogTime(right) - getLogTime(left));
}

function getClientLabel(log) {
  const client = log?.clientName || log?.branchName || log?.companyName || log?.gymName || log?.branch || log?.client;
  if (client && typeof client === "object") {
    return client.name || client.branchName || client.title || "N/A";
  }
  return client || "N/A";
}

function ActivityLog({ companyId, branchId, variant = "client" }) {
  const isTecfolk = variant === "tecfolk";
  const [activityLogs, setActivityLogs] = useState([]);
  const [cachedActivityLogs, setCachedActivityLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [activityType, setActivityType] = useState("All");
  const [activityBy, setActivityBy] = useState("All");
  const [activityByList, setActivityByList] = useState(() => (
    variant === "tecfolk"
      ? [
          { label: "All", value: "All" },
          ...TECFOLK_LOGIN_USERS.map((name) => ({ label: name, value: name }))
        ]
      : []
  ));
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  });
  const [cachedDateFrom, setCachedDateFrom] = useState(dateFrom);
  const [cachedDateTo, setCachedDateTo] = useState(dateTo);

  const activityTypeOptions = [
    { label: "All", value: "All" },
    { label: "Add Fee", value: "Add Fee" },
    { label: "Activate", value: "Activate" },
    { label: "Deactivate", value: "Deactivate" },
    { label: "DeFreeze", value: "DeFreeze" },
    { label: "Delete Fee", value: "Delete Fee" },
    { label: "Freeze", value: "Freeze" },
    { label: "Login", value: "Login User" },
    { label: "Member Settings", value: "Member Settings" },
    { label: "Update Package", value: "Update Package" }
  ];

  const activityByOptions = activityByList.length > 0 ? activityByList : [{ label: "All", value: "All" }];

  const applyFilters = useCallback((logsToFilter, typeFilter, byFilter) => {
    let filtered = logsToFilter;

    // Filter by activity type
    if (typeFilter !== "All") {
      filtered = filtered.filter(log => log.activityType === typeFilter);
    }

    // Filter by activity by
    if (byFilter !== "All") {
      filtered = filtered.filter(log => log.activityBy === byFilter);
    }

    setActivityLogs(filtered);
  }, []);

  const fetchActivityLogs = useCallback(async (type = activityType, by = activityBy, from = dateFrom, to = dateTo) => {
    try {
      setLoading(true);
      setError("");

      if (!isTecfolk && !branchId) {
        setError("Unable to retrieve branch information");
        setLoading(false);
        return;
      }

      const formattedFrom = new Date(from).toISOString().split('T')[0];
      const formattedTo = new Date(to).toISOString().split('T')[0];

      let logs = [];

      if (isTecfolk) {
        logs = await fetchTecfolkActivityLogs(type, by, formattedFrom, formattedTo);
      } else {
        const query = new URLSearchParams({
          activityType: type,
          activityBy: by,
          dateFrom: formattedFrom,
          dateTo: formattedTo,
        });
        const apiUrl = `${getActivityLogsURL(branchId)}?${query.toString()}`;
        const data = await _get(apiUrl, { useCache: false, httpCache: "no-store" });

        if (!data) {
          setError("No data received from server");
          setActivityLogs([]);
          return;
        }

        logs = extractLogList(data);
      }
      console.log("=== ACTIVITY LOG API RESPONSE ===");
      console.log("Parsed Logs Array:", logs);
      if (logs.length > 0) {
        console.log("First Log Object:", logs[0]);
        console.log("All keys in first log:", Object.keys(logs[0]));
        console.log("Date-related fields:", {
          createdDate: logs[0].createdDate,
          activityDate: logs[0].activityDate,
          date: logs[0].date,
          createdAt: logs[0].createdAt,
          timestamp: logs[0].timestamp,
          activity_date: logs[0].activity_date
        });
        console.log("Membership fields:", {
          entityTagNumber: logs[0].entityTagNumber,
          membershipNo: logs[0].membershipNo
        });
      }
      setCachedActivityLogs(logs);
      setCachedDateFrom(from);
      setCachedDateTo(to);

      // Apply filters to the fetched data
      applyFilters(logs, type, by);

      // Extract unique activity by values from logs
      if (logs && logs.length > 0) {
        const uniqueBy = [...new Set(logs.map(log => log.activityBy).filter(Boolean))];
        const names = isTecfolk
          ? [...new Set([...TECFOLK_LOGIN_USERS, ...uniqueBy])]
          : uniqueBy;
        const byOptions = names.map(name => ({ label: name, value: name }));
        setActivityByList([{ label: "All", value: "All" }, ...byOptions]);
      }
    } catch (err) {
      console.error("Error fetching activity logs:", err);
      setError("Failed to load activity logs. Please try again.");
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  }, [activityType, activityBy, dateFrom, dateTo, branchId, applyFilters, isTecfolk]);

  useEffect(() => {
    fetchActivityLogs();
  }, [fetchActivityLogs]);

  // Auto-apply filters when activity type or activity by changes
  useEffect(() => {
    if (cachedActivityLogs.length > 0) {
      applyFilters(cachedActivityLogs, activityType, activityBy);
    }
  }, [activityType, activityBy, cachedActivityLogs, applyFilters]);

  const handleFilter = () => {
    // Check if date range changed
    if (dateFrom !== cachedDateFrom || dateTo !== cachedDateTo) {
      // Date changed - fetch from API
      fetchActivityLogs(activityType, activityBy, dateFrom, dateTo);
    } else {
      // Only filters changed - apply to cached data
      applyFilters(cachedActivityLogs, activityType, activityBy);
    }
  };

  const handleReset = () => {
    setActivityType("All");
    setActivityBy("All");
    const defaultFrom = new Date();
    defaultFrom.setDate(1);
    const defaultTo = new Date();
    defaultTo.setMonth(defaultTo.getMonth() + 1);
    defaultTo.setDate(0);
    const fromStr = defaultFrom.toISOString().split('T')[0];
    const toStr = defaultTo.toISOString().split('T')[0];
    setDateFrom(fromStr);
    setDateTo(toStr);
    fetchActivityLogs("All", "All", fromStr, toStr);
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      console.warn("formatDate called with empty/null value:", dateString);
      return "N/A";
    }
    try {
      // Handle both ISO string and other formats
      let date;
      if (typeof dateString === 'string') {
        // Add 'Z' if no timezone specified
        const dateStr = dateString.includes('T') && !dateString.includes('Z') ? dateString + 'Z' : dateString;
        date = new Date(dateStr);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        console.warn("Invalid date after parsing:", dateString, "parsed as:", date);
        return "N/A";
      }

      const formatted = date.toLocaleString("en-GB", {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      console.log("Date formatted successfully:", dateString, "=>", formatted);
      return formatted;
    } catch (e) {
      console.error("Error formatting date:", e, "input:", dateString);
      return "N/A";
    }
  };

  return (
    <div className="configMain" style={{ padding: "20px" }}>
      <style>{`
        @keyframes slideInBounce {
          0% {
            opacity: 0;
            transform: translateX(20px) scale(0.8);
          }
          50% {
            transform: translateX(-5px) scale(1.05);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        .activity-log-container {
          background: #1a1d23;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        }

        .filter-section {
          background: #2a2d35;
          border-bottom: 1px solid #444;
          padding: 20px;
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          align-items: flex-end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 150px;
        }

        .filter-group label {
          font-size: 13px;
          color: #888;
          margin-bottom: 8px;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .filter-group select,
        .filter-group input {
          padding: 10px 12px;
          background: #1a1d23;
          border: 1px solid #444;
          border-radius: 6px;
          color: #fff;
          font-size: 14px;
          font-family: inherit;
          transition: all 0.3s ease;
        }

        .filter-group select:hover,
        .filter-group input:hover {
          border-color: #187718;
          background: #33363f;
        }

        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #187718;
          box-shadow: 0 0 0 2px rgba(24, 119, 24, 0.2);
          background: #33363f;
        }

        .button-group {
          display: flex;
          gap: 10px;
          min-width: 250px;
          margin-left: auto;
        }

        .btn {
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .btn-filter {
          background: #187718;
          color: #fff;
        }

        .btn-filter:hover {
          background: #20a020;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(24, 119, 24, 0.3);
        }

        .btn-reset {
          background: #444;
          color: #fff;
        }

        .btn-reset:hover {
          background: #555;
          transform: translateY(-2px);
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .table-wrapper {
          overflow-x: auto;
          max-width: 100%;
          min-width: 0;
          -webkit-overflow-scrolling: touch;
        }

        @media (max-width: 700px) {
          .filter-section {
            padding: 14px;
          }

          .filter-group {
            flex: 1 1 100%;
            min-width: 0;
          }

          .button-group {
            width: 100%;
            min-width: 0;
            margin-left: 0;
          }

          .button-group .btn {
            flex: 1;
          }

          .activity-table {
            min-width: 720px;
          }
        }

        .activity-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .activity-table thead {
          background: #2a2d35;
          border-bottom: 2px solid #187718;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .activity-table th {
          padding: 14px 12px;
          text-align: left;
          font-weight: 600;
          color: #4ade80;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 12px;
        }

        .activity-table td {
          padding: 12px;
          border-bottom: 1px solid #333;
          color: #ccc;
        }

        .activity-table tbody tr {
          transition: all 0.3s ease;
        }

        .activity-table tbody tr:hover {
          background: #2a2d35;
        }

        .activity-table tbody tr:last-child td {
          border-bottom: none;
        }

        .activity-date {
          color: #888;
          font-size: 12px;
        }

        .activity-type {
          background: rgba(24, 119, 24, 0.2);
          color: #4ade80;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
          font-weight: 600;
        }

        .activity-scope {
          background: rgba(30, 58, 138, 0.2);
          color: #60a5fa;
          padding: 4px 8px;
          border-radius: 4px;
          display: inline-block;
          font-weight: 600;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #888;
        }

        .empty-state svg {
          width: 60px;
          height: 60px;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-state h3 {
          font-size: 18px;
          margin: 0 0 8px;
          color: #aaa;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #fca5a5;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .loading-spinner {
          display: inline-block;
          width: 16px;
          height: 16px;
          border: 2px solid #187718;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .loading-state {
          text-align: center;
          padding: 40px 20px;
          color: #888;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
      `}</style>

      <div className="activity-log-container">
        {isTecfolk && (
          <h1 style={{ margin: "0 0 16px", fontSize: "28px", fontWeight: 700 }}>
            Tecfolk Activity
          </h1>
        )}
        {/* Filter Section */}
        <div className="filter-section">
          <div className="filter-group">
            <label>Activity Type</label>
            <select
              value={activityType}
              onChange={(e) => setActivityType(e.target.value)}
            >
              {activityTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Activity By</label>
            <select
              value={activityBy}
              onChange={(e) => setActivityBy(e.target.value)}
            >
              {activityByOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="filter-group button-group">
            <button
              className="btn btn-filter"
              onClick={handleFilter}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span> Searching...
                </>
              ) : (
                "Search"
              )}
            </button>
            <button
              className="btn btn-reset"
              onClick={handleReset}
              disabled={loading}
            >
              Reset
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <span className="loading-spinner"></span>
            Loading activity logs...
          </div>
        )}

        {/* Table or Empty State */}
        {!loading && activityLogs.length === 0 && !error && (
          <div className="empty-state">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
            </svg>
            <h3>No activity logs found</h3>
            <p>Try adjusting your filter criteria</p>
          </div>
        )}

        {!loading && activityLogs.length > 0 && (
          <div className="table-wrapper">
            <table className="activity-table">
              <thead>
                <tr>
                  <th>Activity Date</th>
                  {isTecfolk && <th>Client</th>}
                  <th>Activity Type</th>
                  <th>Activity By</th>
                  <th>Initiated From</th>
                  <th>Membership No</th>
                  <th>Subject</th>
                  <th>Scope</th>
                  <th>Old Value</th>
                  <th>New Value</th>
                </tr>
              </thead>
              <tbody>
                {activityLogs.map((log, index) => {
                  const dateValue = log.createdDate || log.activityDate || log.date || log.createdAt || log.timestamp || log.activity_date;
                  if (index === 0) {
                    console.log("First row - Full log object:", log);
                    console.log("All fields in log:", Object.entries(log).map(([key, value]) => `${key}: ${value}`).join(", "));
                    console.log("Membership Number Debug - entityTagNumber:", log.entityTagNumber, "All values:", log);
                  }
                  return (
                  <tr key={index}>
                    <td className="activity-date">
                      {formatDate(dateValue)}
                    </td>
                    {isTecfolk && <td>{getClientLabel(log)}</td>}
                    <td>
                      <span className="activity-type">{log.activityType || "N/A"}</span>
                    </td>
                    <td>{log.activityBy || "N/A"}</td>
                    <td>{log.initiatedFrom || "N/A"}</td>
                    <td>{log.entityTagNumber && log.entityTagNumber !== "" ? log.entityTagNumber : "N/A"}</td>
                    <td>{log.subject || "N/A"}</td>
                    <td>
                      <span className="activity-scope">{log.scope || "N/A"}</span>
                    </td>
                    <td>{log.oldValue || "N/A"}</td>
                    <td>{log.newValue || "N/A"}</td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityLog;
